import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Container, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AppHeader from '../AppHeader';
import { useClearOnSiteChange, useCurrentSiteId } from '../../hooks/useCurrentSiteId';
import { programAPI, saveBlob, sessionAPI } from '../../services/api';
import {
  caseloadEmailText, caseloadEmptyText, compareClassNum, nextSort, sortRows,
} from './programGridSort';

const WOODMERE_COLUMNS = [
  'last_name', 'first_name', 'class_num', 'date_of_birth', 'special_alert', 'funding_code',
  'school_district', 'enrollment_date', 'discharge_date', 'teacher', 'room', 'assistant_1',
  'assistant_2', 'aide_1to1', 'speech', 'ot', 'pt', 'counseling', 'parent_training', 'social_worker',
  'bus_am', 'bus_pm', 'iep_start', 'iep_end', 'iep_received', 'notes', 'rx_physician_name', 'osis_id',
];

const EDITABLE = new Set([
  'special_alert', 'notes', 'aide_1to1', 'osis_id', 'funding_code', 'school_district', 'rx_physician_name',
]);

function serviceText(services, type) {
  return (services || []).filter((row) => row.service_type === type).map((row) => (
    `${row.frequency}x${row.duration_minutes} ${row.delivery}${row.rx_date ? ` Rx ${row.rx_date}` : ''}${row.therapist ? ` ${row.therapist}` : ''}`
  )).join('; ');
}

function display(row, column) {
  if (column === 'speech') return serviceText(row.services, 'SP');
  if (column === 'ot') return serviceText(row.services, 'OT');
  if (column === 'pt') return serviceText(row.services, 'PT');
  if (column === 'counseling') return serviceText(row.services, 'COUNSELING');
  if (column === 'parent_training') return serviceText(row.services, 'PARENT_TRAINING');
  if (column === 'social_worker') return serviceText(row.services, 'SW');
  const value = row[column];
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return value ?? '';
}

function download(blob, filename) {
  saveBlob(blob, filename);
}

export function ProgramGridPage() {
  const siteId = useCurrentSiteId();
  const [rows, setRows] = useState([]);
  const [help, setHelp] = useState('');
  const [attention, setAttention] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('programGridColumns') || 'null') || WOODMERE_COLUMNS;
    } catch (err) {
      return WOODMERE_COLUMNS;
    }
  });
  const [query, setQuery] = useState('');
  const [emptyColumn, setEmptyColumn] = useState('');
  const [notEmptyColumn, setNotEmptyColumn] = useState('');
  const [classNum, setClassNum] = useState('');
  const [sort, setSort] = useState({ column: null, direction: null });
  const [viewName, setViewName] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [mandate, setMandate] = useState(null);
  const [comment, setComment] = useState('');
  const [effective, setEffective] = useState('');

  useClearOnSiteChange(siteId, () => {
    setRows([]);
    setHelp('');
    setAttention([]);
    setError('');
    setLoading(true);
    setQuery('');
    setEmptyColumn('');
    setNotEmptyColumn('');
    setClassNum('');
    setSort({ column: null, direction: null });
    setViewName('');
    setMandate(null);
  });

  const load = () => setReloadKey((value) => value + 1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    programAPI.getGrid().then((res) => {
      if (cancelled) return;
      setRows(res.data.rows || []);
      setHelp(res.data.help || '');
      setAttention(res.data.needs_attention || []);
      setLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      setRows([]);
      setError(err.response?.data?.detail || 'The grid did not load.');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [siteId, reloadKey]);

  const classOptions = useMemo(() => {
    const values = [...new Set(rows.map((row) => row.class_num).filter((value) => String(value ?? '').trim() !== ''))];
    values.sort(compareClassNum);
    return values;
  }, [rows]);

  const visible = useMemo(() => {
    const filtered = rows.filter((row) => {
      if (classNum && String(row.class_num ?? '') !== String(classNum)) return false;
      if (emptyColumn && String(display(row, emptyColumn) || '') !== '') return false;
      if (notEmptyColumn && String(display(row, notEmptyColumn) || '') === '') return false;
      if (!query) return true;
      return columns.some((column) => String(display(row, column)).toLowerCase().includes(query.toLowerCase()));
    });
    return sortRows(filtered, sort, display);
  }, [rows, columns, query, emptyColumn, notEmptyColumn, classNum, sort]);

  const saveColumns = (next) => {
    setColumns(next);
    localStorage.setItem('programGridColumns', JSON.stringify(next));
  };

  const onBlur = (row, column, value) => {
    if (!EDITABLE.has(column) || String(row[column] ?? '') === value) return;
    programAPI.patchCell(row.id, column, value).then(load).catch((err) => setError(err.response?.data?.detail || 'That cell did not save.'));
  };

  const exportFile = (format) => {
    const payloadRows = visible.map((row) => {
      const out = { id: row.id };
      columns.forEach((column) => { out[column] = display(row, column); });
      return out;
    });
    programAPI.exportGrid({ format, columns, rows: payloadRows }).then((res) => {
      download(res.data, `program-grid.${format === 'xlsx' ? 'xlsx' : format}`);
    }).catch(() => setError('The export did not download.'));
  };

  return (
    <>
      <AppHeader backTo="/" rightContent={<Typography variant="subtitle1">Program grid</Typography>} />
      <Container maxWidth={false} sx={{ py: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>{help || 'Filter any column, including empty and not empty. This replaces Query Design. A mandate edit asks for a comment and a date. Other edits save when you leave the cell.'}</Alert>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {attention.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {attention.length} mandate{attention.length === 1 ? '' : 's'} need attention (carried and unconfirmed, or a prescription missing or expired).
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <TextField size="small" label="Find" value={query} onChange={(e) => setQuery(e.target.value)} />
          <TextField size="small" select label="Class" value={classNum} onChange={(e) => setClassNum(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">All</MenuItem>
            {classOptions.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Is empty" value={emptyColumn} onChange={(e) => setEmptyColumn(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">Any</MenuItem>
            {WOODMERE_COLUMNS.map((column) => <MenuItem key={column} value={column}>{column}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Is not empty" value={notEmptyColumn} onChange={(e) => setNotEmptyColumn(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">Any</MenuItem>
            {WOODMERE_COLUMNS.map((column) => <MenuItem key={column} value={column}>{column}</MenuItem>)}
          </TextField>
          <Button variant="outlined" onClick={() => saveColumns(WOODMERE_COLUMNS)}>Woodmere default</Button>
          <Button variant="outlined" onClick={() => exportFile('csv')}>CSV</Button>
          <Button variant="outlined" onClick={() => exportFile('xlsx')}>Excel</Button>
          <Button variant="outlined" onClick={() => exportFile('pdf')}>PDF</Button>
          <TextField size="small" label="Save view as" value={viewName} onChange={(e) => setViewName(e.target.value)} />
          <Button onClick={() => programAPI.saveView({
            name: viewName,
            columns,
            filters: { query, emptyColumn, notEmptyColumn, classNum, sort },
          })}
          >Save view</Button>
        </Box>
        <Paper sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column} sortDirection={sort.column === column ? sort.direction || false : false}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        size="small"
                        checked={columns.includes(column)}
                        inputProps={{ 'aria-label': `Hide ${column}` }}
                        onChange={() => saveColumns(columns.filter((item) => item !== column))}
                      />
                      <TableSortLabel
                        active={sort.column === column && Boolean(sort.direction)}
                        direction={sort.column === column && sort.direction ? sort.direction : 'asc'}
                        onClick={() => setSort((current) => nextSort(current, column))}
                      >
                        {column}
                      </TableSortLabel>
                    </Box>
                  </TableCell>
                ))}
                <TableCell>Mandate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((row) => (
                <TableRow key={row.id}>
                  {columns.map((column) => (
                    <TableCell key={column}>
                      {EDITABLE.has(column) ? (
                        <TextField
                          size="small"
                          defaultValue={row[column] || ''}
                          key={`${row.id}-${column}-${row[column] || ''}`}
                          onBlur={(e) => onBlur(row, column, e.target.value)}
                        />
                      ) : display(row, column)}
                    </TableCell>
                  ))}
                  <TableCell>
                    {(row.services || []).map((service) => (
                      <Button key={service.id} size="small" onClick={() => setMandate(service)}>
                        {service.service_type}
                      </Button>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && visible.length === 0 && (
            <Typography sx={{ p: 2 }}>No students in this view.</Typography>
          )}
        </Paper>
        <Dialog open={Boolean(mandate)} onClose={() => setMandate(null)}>
          <DialogTitle>Change this mandate</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>A comment and the date the change takes effect are required.</Typography>
            <TextField fullWidth label="Comment" value={comment} onChange={(e) => setComment(e.target.value)} sx={{ mt: 1 }} />
            <TextField fullWidth type="date" label="Effective date" InputLabelProps={{ shrink: true }} value={effective} onChange={(e) => setEffective(e.target.value)} sx={{ mt: 2 }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMandate(null)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!comment.trim() || !effective}
              onClick={() => {
                programAPI.updateMandate(mandate.id, { comment, effective_date: effective }).then(() => {
                  setMandate(null);
                  setComment('');
                  load();
                }).catch((err) => setError(err.response?.data?.detail || 'The mandate did not save.'));
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}

export function ReportsIndexPage() {
  const links = [
    ['Caseload by therapist', '/reports/caseload'],
    ['Class roster', '/reports/class-roster'],
    ['Students with / without a value', '/program'],
    ['Medical due', '/reports/medical-due'],
    ['Staff compliance due', '/reports/staff-compliance'],
    ['Nurse medication expiry', '/reports/nurse-medications'],
    ['CC reports', null],
    ['SC reports', null],
    ['Monthly summary sheet', null],
  ];
  return (
    <>
      <AppHeader backTo="/" rightContent={<Typography variant="subtitle1">Reports</Typography>} />
      <Container sx={{ py: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>Reports are read-only. Type a teacher or therapist name on the program grid; the report reads that change. CC, SC, and the monthly summary wait for the sample layouts.</Alert>
        {links.map(([label, path]) => (
          <Paper key={label} sx={{ p: 2, mb: 1 }}>
            {path ? <Button component={RouterLink} to={path}>{label}</Button> : <Typography>{label} — waiting on the sample</Typography>}
          </Paper>
        ))}
      </Container>
    </>
  );
}

export function CaseloadReportPage() {
  const siteId = useCurrentSiteId();
  const [discipline, setDiscipline] = useState('OT');
  const [therapist, setTherapist] = useState('all');
  const [sections, setSections] = useState([]);
  const [meta, setMeta] = useState({ session_name: '', site_name: '', site_email_enabled: false });
  const [loading, setLoading] = useState(true);
  useClearOnSiteChange(siteId, () => {
    setSections([]);
    setTherapist('all');
    setLoading(true);
  });
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    programAPI.caseload({ discipline, therapist: 'all' }).then((res) => {
      if (cancelled) return;
      setSections(res.data.sections || []);
      setMeta({
        session_name: res.data.session_name || '',
        site_name: res.data.site_name || '',
        site_email_enabled: Boolean(res.data.site_email_enabled),
      });
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [siteId, discipline]);
  const shown = therapist === 'all'
    ? sections
    : sections.filter((section) => String(section.therapist_id ?? '0') === String(therapist));
  const rowCount = shown.reduce((count, section) => count + (section.rows || []).length, 0);
  const exportFile = (format) => {
    if (!rowCount) return;
    programAPI.caseloadFile({ discipline, therapist, export: format }).then((res) => {
      download(res.data, `caseload.${format === 'xlsx' ? 'xlsx' : format}`);
    });
  };
  return (
    <>
      <AppHeader backTo="/reports" rightContent={<Typography variant="subtitle1">Caseload by therapist</Typography>} />
      <Container sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <TextField select size="small" label="Discipline" value={discipline} onChange={(e) => { setDiscipline(e.target.value); setTherapist('all'); }}>
            {['all', 'SP', 'OT', 'PT', 'COUNSELING', 'PARENT_TRAINING', 'SW'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Therapist" value={sections.length ? therapist : 'none'} onChange={(e) => setTherapist(e.target.value)} sx={{ minWidth: 220 }}>
            {sections.length === 0
              ? <MenuItem value="none">(no therapists)</MenuItem>
              : [
                <MenuItem key="all" value="all">All</MenuItem>,
                ...sections.map((section) => (
                  <MenuItem key={section.therapist_id || section.therapist} value={section.therapist_id ?? '0'}>{section.therapist}</MenuItem>
                )),
              ]}
          </TextField>
          <Button disabled={!rowCount || loading} onClick={() => exportFile('xlsx')}>Excel</Button>
          <Button disabled={!rowCount || loading} onClick={() => exportFile('pdf')}>PDF</Button>
          <Button disabled={!rowCount || loading} onClick={() => exportFile('csv')}>CSV</Button>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>{caseloadEmailText(meta.site_email_enabled)} This report is read-only.</Alert>
        {!loading && rowCount === 0 && (
          <Alert severity="info">{caseloadEmptyText(discipline, meta.session_name, meta.site_name)}</Alert>
        )}
        {shown.map((section) => (
          <Paper key={section.therapist_id || section.therapist} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">{section.therapist}</Typography>
            <Table size="small">
              <TableHead><TableRow><TableCell>Child</TableCell><TableCell>Class</TableCell><TableCell>Teacher</TableCell><TableCell>Mandate</TableCell><TableCell>Rx</TableCell></TableRow></TableHead>
              <TableBody>
                {section.rows.map((row) => (
                  <TableRow key={`${row.student_id}-${row.service_type}-${row.mandate}`}>
                    <TableCell>{row.child}</TableCell><TableCell>{row.class}</TableCell><TableCell>{row.teacher}</TableCell><TableCell>{row.mandate}</TableCell><TableCell>{row.rx_date || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        ))}
      </Container>
    </>
  );
}

export function ClassRosterReportPage() {
  const siteId = useCurrentSiteId();
  const [classNum, setClassNum] = useState('all');
  const [classes, setClasses] = useState([]);
  const [meta, setMeta] = useState({ session_name: '', site_name: '' });
  const [loading, setLoading] = useState(true);
  useClearOnSiteChange(siteId, () => {
    setClasses([]);
    setClassNum('all');
    setLoading(true);
  });
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    programAPI.classRoster({ class: 'all' }).then((res) => {
      if (cancelled) return;
      setClasses(res.data.classes || []);
      setMeta({ session_name: res.data.session_name || '', site_name: res.data.site_name || '' });
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [siteId]);
  const shown = classNum === 'all' ? classes : classes.filter((room) => String(room.class_num) === String(classNum));
  return (
    <>
      <AppHeader backTo="/reports" rightContent={<Typography variant="subtitle1">Class roster</Typography>} />
      <Container sx={{ py: 2 }}>
        <TextField select size="small" label="Class" value={classNum} onChange={(e) => setClassNum(e.target.value)} sx={{ mb: 2, minWidth: 180 }}>
          <MenuItem value="all">all</MenuItem>
          {classes.map((room) => <MenuItem key={room.class_num} value={room.class_num}>Class {room.class_num}</MenuItem>)}
        </TextField>
        {!loading && classes.length === 0 && (
          <Alert severity="info">No classes in {meta.session_name} for {meta.site_name}</Alert>
        )}
        {shown.map((room) => (
          <Paper key={room.class_num} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Class {room.class_num} — {room.teacher} — room {room.room} — {room.ratio}</Typography>
            <Typography variant="body2">Assistants: {room.assistant_1} {room.assistant_2}</Typography>
            {room.class_num_only > 0 && (
              <Typography variant="body2">
                {room.class_num_only} children listed by class number only — run backfill_classroom_enrollments
              </Typography>
            )}
            <Table size="small">
              <TableHead><TableRow><TableCell>Child</TableCell><TableCell>DOB</TableCell><TableCell>Alert</TableCell><TableCell>1:1</TableCell><TableCell>Services</TableCell></TableRow></TableHead>
              <TableBody>
                {room.children.map((child) => (
                  <TableRow key={child.student_id}><TableCell>{child.child}</TableCell><TableCell>{child.dob}</TableCell><TableCell>{child.alert}</TableCell><TableCell>{child.aide_1to1}</TableCell><TableCell>{child.services}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        ))}
      </Container>
    </>
  );
}

export function StaffComplianceReportPage() {
  const siteId = useCurrentSiteId();
  const [windowDays, setWindowDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ session_name: '', site_name: '' });
  const [loading, setLoading] = useState(true);
  useClearOnSiteChange(siteId, () => {
    setRows([]);
    setLoading(true);
  });
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    programAPI.staffCompliance({ window: windowDays }).then((res) => {
      if (cancelled) return;
      setRows(res.data.rows || []);
      setMeta({ session_name: res.data.session_name || '', site_name: res.data.site_name || '' });
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [siteId, windowDays]);
  return (
    <>
      <AppHeader backTo="/reports" rightContent={<Typography variant="subtitle1">Staff compliance due</Typography>} />
      <Container sx={{ py: 2 }}>
        <TextField select size="small" label="Window" value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))} sx={{ mb: 2 }}>
          {[14, 30, 60, 90].map((days) => <MenuItem key={days} value={days}>{days} days</MenuItem>)}
        </TextField>
        {!loading && rows.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No staff compliance items due in the next {windowDays} days in {meta.session_name} for {meta.site_name}
          </Alert>
        )}
        <Table size="small">
          <TableHead><TableRow><TableCell>Employee</TableCell><TableCell>Kind</TableCell><TableCell>Expires</TableCell><TableCell>Days left</TableCell></TableRow></TableHead>
          <TableBody>{rows.map((row) => <TableRow key={`${row.employee_id}-${row.kind}`}><TableCell>{row.employee}</TableCell><TableCell>{row.kind}</TableCell><TableCell>{row.expires}</TableCell><TableCell>{row.days_left}</TableCell></TableRow>)}</TableBody>
        </Table>
      </Container>
    </>
  );
}

export function NurseMedicationReportPage() {
  const siteId = useCurrentSiteId();
  const [windowDays, setWindowDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ session_name: '', site_name: '' });
  const [loading, setLoading] = useState(true);
  useClearOnSiteChange(siteId, () => {
    setRows([]);
    setLoading(true);
  });
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    programAPI.nurseMedications({ window: windowDays }).then((res) => {
      if (cancelled) return;
      setRows(res.data.rows || []);
      setMeta({ session_name: res.data.session_name || '', site_name: res.data.site_name || '' });
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [siteId, windowDays]);
  return (
    <>
      <AppHeader backTo="/reports" rightContent={<Typography variant="subtitle1">Nurse medication expiry</Typography>} />
      <Container sx={{ py: 2 }}>
        <TextField select size="small" label="Window" value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))} sx={{ mb: 2 }}>
          {[30, 60, 90].map((days) => <MenuItem key={days} value={days}>{days} days</MenuItem>)}
        </TextField>
        {!loading && rows.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No nurse medications expiring in the next {windowDays} days in {meta.session_name} for {meta.site_name}
          </Alert>
        )}
        <Table size="small">
          <TableHead><TableRow><TableCell>Child</TableCell><TableCell>Medication</TableCell><TableCell>Which date</TableCell><TableCell>Expires</TableCell></TableRow></TableHead>
          <TableBody>{rows.map((row, index) => <TableRow key={index}><TableCell>{row.child}</TableCell><TableCell>{row.medication}</TableCell><TableCell>{row.which_date}</TableCell><TableCell>{row.expires}</TableCell></TableRow>)}</TableBody>
        </Table>
      </Container>
    </>
  );
}

export function RolloverPage() {
  const [sessions, setSessions] = useState([]);
  const [source, setSource] = useState('');
  const [target, setTarget] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { sessionAPI.getSessions().then((res) => setSessions(res.data.results || res.data || [])); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <AppHeader backTo="/" rightContent={<Typography variant="subtitle1">Term rollover</Typography>} />
      <Container sx={{ py: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>Active students, classrooms, mandates, bus assignments, and the nurse checklist can carry. Consents do not. Carried mandates stay unconfirmed until someone confirms them.</Alert>
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField select label="Source session" value={source} onChange={(e) => setSource(e.target.value)} sx={{ minWidth: 240 }}>
            {sessions.map((session) => <MenuItem key={session.id} value={session.id}>{session.name}</MenuItem>)}
          </TextField>
          <TextField select label="Target session" value={target} onChange={(e) => setTarget(e.target.value)} sx={{ minWidth: 240 }}>
            {sessions.map((session) => <MenuItem key={session.id} value={session.id}>{session.name}</MenuItem>)}
          </TextField>
          <Button variant="contained" onClick={() => programAPI.rollover({
            source_session: source,
            target_session: target,
            carry: { students: 'active', classrooms: true, mandates: true, bus: true, medical_forms: true },
          }).then((res) => setResult(res.data)).catch((err) => setError(err.response?.data?.detail || 'Rollover did not run.'))}>Run</Button>
        </Box>
        {result && <Typography sx={{ mt: 2 }}>{result.students} students copied into session {result.target_session_id}.</Typography>}
      </Container>
    </>
  );
}

function studentsMatching(rows, filters, columns) {
  const emptyColumn = filters.emptyColumn || '';
  const notEmptyColumn = filters.notEmptyColumn || '';
  const query = filters.query || '';
  const classNum = filters.classNum || '';
  const matched = rows.filter((row) => {
    if (classNum && String(row.class_num ?? '') !== String(classNum)) return false;
    if (emptyColumn && String(display(row, emptyColumn) || '') !== '') return false;
    if (notEmptyColumn && String(display(row, notEmptyColumn) || '') === '') return false;
    if (!query) return true;
    return columns.some((column) => String(display(row, column)).toLowerCase().includes(query.toLowerCase()));
  });
  return sortRows(matched, filters.sort || null, display);
}

export function PrintMergeCard() {
  const siteId = useCurrentSiteId();
  const [body, setBody] = useState('{child}\n{parents}\n{address}\n{city_state_zip}\nClass {class_num}\n{teacher}\nRoom {room}\nAM {bus_am}  PM {bus_pm}');
  const [ids, setIds] = useState('');
  const [views, setViews] = useState([]);
  const [viewId, setViewId] = useState('');
  const [error, setError] = useState('');

  useClearOnSiteChange(siteId, () => {
    setViews([]);
    setViewId('');
    setError('');
  });

  useEffect(() => {
    let cancelled = false;
    programAPI.savedViews().then((res) => {
      if (!cancelled) setViews(res.data || []);
    }).catch(() => {
      if (!cancelled) setViews([]);
    });
    return () => { cancelled = true; };
  }, [siteId]);

  const printStudents = (studentIds) => {
    if (!studentIds.length) {
      setError('No students matched that view.');
      return;
    }
    programAPI.printMerge({ students: studentIds, body }).then((res) => {
      download(res.data, 'print-merge.pdf');
    }).catch(() => setError('The letter did not print.'));
  };

  const printSavedView = () => {
    const view = views.find((item) => String(item.id) === String(viewId));
    if (!view) {
      setError('Choose a saved view.');
      return;
    }
    const columns = view.columns && view.columns.length ? view.columns : WOODMERE_COLUMNS;
    programAPI.getGrid().then((res) => {
      const matched = studentsMatching(res.data.rows || [], view.filters || {}, columns);
      printStudents(matched.map((row) => row.id));
    }).catch(() => setError('The saved view could not be loaded.'));
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6">Print merge</Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>One page per family, for the printer. This does not send email or a text.</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <TextField fullWidth multiline minRows={3} label="Letter" value={body} onChange={(e) => setBody(e.target.value)} sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <TextField select size="small" label="Saved view" value={viewId} onChange={(e) => setViewId(e.target.value)} sx={{ minWidth: 220 }}>
          <MenuItem value="">None</MenuItem>
          {views.map((view) => <MenuItem key={view.id} value={view.id}>{view.name}</MenuItem>)}
        </TextField>
        <Button variant="outlined" onClick={printSavedView}>Download PDF for this view</Button>
      </Box>
      <TextField fullWidth size="small" label="Or student ids, comma separated" value={ids} onChange={(e) => setIds(e.target.value)} />
      <Button sx={{ mt: 1 }} variant="outlined" onClick={() => printStudents(ids.split(',').map((item) => Number(item.trim())).filter(Boolean))}>Download PDF</Button>
    </Paper>
  );
}
